import Chip, { Version } from '../chip/Chip';
import ChipList from '../chip-list/ChipList';
import StatusHeader from '../status-header/StatusHeader';
import Button, { ButtonThemes } from '../button/Button';
import Description from '../description/Description';
import useDocumentEvents from '../../elements/hooks/useDocumentEvents';
import ListItems from '../list-items/ListItems';
import Modal from '../modal/Modal';
import Alert from '../alert/Alert';
import { useIsVisible } from '../../features/dashboards/admin/hooks/useIsVisible';
import { useMemo, FC, useCallback, useState } from 'react';
import { readableFileSize } from '../../helpers/fileHelpers';
import { formatDate } from '../../helpers/dateHelpers';
import { getFinalDocumentStatus, getStatusLabelType } from '../../helpers/statusHelpers';
import { TestIds } from '../../mocks/ids';
import { FileUploadStatus } from '../../hooks/useFileUpload';
import { withUIState } from '../../contexts/ui-state/withUIState';
import { DocumentInfoModel } from '../../models/ChatTypes';
import styles from './FileList.module.scss';
import { Icons } from '../icon/icons/material';
// import { Icon } from '../icon/Icon';
// import { Icons } from '../icon/icons/material';


export type FileListProps = {
    chipId?: string;
    isLoading?: boolean;

    labels: {
        processingErrorHeadline: () => string;
        processingErrorDescription: (stage: string) => string;

        close: string;

        chipStates: {
            ready: string;
            error: string;
            processing: string;
            uploaded: string;
        };

        seeErrorDetails: string;
        addDescriptionCta: string;
        tableConfigurationCta: string;
        seeMore: string;
        seeLess: string;
    };

    files: DocumentInfoModel[];
    loadingIds: string[];
    selectedIds: string[];
    isCheckboxDisabled?: boolean;
    isButtonDisabled?: boolean;
    onToggleSelectId?: (id: string) => void;
    onEdit?: ({ name, description, fileId }: { name: string, description: string, fileId: string; }) => void;
    showTableSearchConfig?: boolean;
    showDescriptionCta?: boolean;
};

const FileList: FC<FileListProps> = ({ isLoading, chipId, isCheckboxDisabled, isButtonDisabled, labels, files, selectedIds, loadingIds, onEdit, showTableSearchConfig, onToggleSelectId, showDescriptionCta = true }) => {
    const [selectedFile, setSelectedFile] = useState<DocumentInfoModel>();
    const { isOpen, onClose, onShow } = useIsVisible();

    const { eventDictionary } = useDocumentEvents();

    const handleStateClicked = useCallback((file: DocumentInfoModel) => {
        onShow();
        const selectedDocumentEvent = eventDictionary[file.id];
        const selectedDocument = files.find((f) => f.id === file.id);
        const data = { ...selectedDocument, state: { ...selectedDocument?.state, error: { ...selectedDocument?.state?.error, ...selectedDocumentEvent?.metadata } } } as DocumentInfoModel;
        setSelectedFile(data);
    }, [eventDictionary, files, onShow]);

    const handleClose = useCallback(() => {
        setSelectedFile(undefined);
        onClose();
    }, [onClose]);

    const filesList = useMemo(() => files.map((document) => {
        const { size, format: extension, id, created, display: { name = '', description = '' } = {}, state: { status } = {}, is_tabular } = document || {};

        const base = { id, name, description, status, size, extension, onItemSelect: onToggleSelectId };
        let icon: Icons | undefined = undefined;
        if (showTableSearchConfig && document.state?.table_search_configured) {
            icon = Icons.grid;
        }
        const calculatedStatus = getFinalDocumentStatus(document, eventDictionary);

        const statusLabelMap = () => {
            switch (calculatedStatus) {
                case FileUploadStatus.Error: return labels.chipStates.error;
                case FileUploadStatus.Processing: return labels.chipStates.processing;
                case FileUploadStatus.Ready: return labels.chipStates.ready;
                case FileUploadStatus.Uploaded: return labels.chipStates.uploaded;
                default: return 'N/A';
            }
        };

        const footer = (
            <>
                {
                    calculatedStatus === FileUploadStatus.Error ? <Button
                        isDisabled={isButtonDisabled || isLoading}
                        className={styles['description-cta']}
                        theme={ButtonThemes.textPrimary}
                        label={labels.seeErrorDetails}
                        onClick={() => handleStateClicked(document)}
                    />
                        :
                        is_tabular && showTableSearchConfig && calculatedStatus !== FileUploadStatus.Ready ?
                            <>
                                {
                                    <Button
                                        isDisabled={isButtonDisabled || isLoading}
                                        className={styles['description-cta']}
                                        theme={ButtonThemes.textPrimary}
                                        // icon={<Icon.Adaptive title='' iconName={Icons.warning} />}
                                        // iconPosition={ButtonIconPosition.beginning}
                                        label={`${calculatedStatus === FileUploadStatus.Uploaded ? '⚠️ ' : ''}${labels.tableConfigurationCta}`}
                                        onClick={() => onEdit?.({ name, description, fileId: id })}
                                    />
                                }
                                {
                                    description && <Description
                                        threshold={140}
                                        description={description}
                                        labels={labels}
                                    />
                                }
                            </> : !description && onEdit
                                ? showDescriptionCta && <Button
                                    isDisabled={isButtonDisabled || isLoading}
                                    className={styles['description-cta']}
                                    theme={ButtonThemes.textPrimary}
                                    label={labels.addDescriptionCta}
                                    onClick={() => onEdit?.({ name, description, fileId: id })}
                                />
                                : <Description
                                    threshold={140}
                                    description={description}
                                    labels={labels}
                                />

                }
            </>);

        const children = (<div className={styles['chip-wrapper']}>
            <Chip
                id={chipId}
                version={Version.minified}
                className={styles.progress}
                label={statusLabelMap()}
                theme={getStatusLabelType(calculatedStatus)}
            />
            <span className={styles.separator}>|</span>
            <ChipList
                minChipWidth={30}
                chips={[
                    { label: readableFileSize(size) },
                    { label: extension },
                    { label: formatDate?.(created, 'en', true) }
                ]} />
        </div>
        );

        const header = <StatusHeader headline={name} icon={icon} />;

        return { ...base, header, footer, children, };
    }), [files, onToggleSelectId, eventDictionary, isButtonDisabled, isLoading, labels, onEdit, showTableSearchConfig, showDescriptionCta, chipId, handleStateClicked]);

    if (!filesList.length) return null;

    return (
        <div data-testid={TestIds.documentList} className={styles['file-list']}>
            <ListItems
                isDisabled={isCheckboxDisabled || isLoading}
                data-testid={TestIds.documentListItems}
                childrenClassName={styles.children}
                headerClassName={styles.header}
                itemsSelected={selectedIds}
                loadingItemIds={loadingIds}
                items={filesList}
            />
            <Modal
                closeOnEscape
                headline={labels.processingErrorHeadline()}
                body={<>
                    <Alert
                        type={'error'}
                        collapseDetails
                        detailsClassName={styles.details}
                        headline={labels.processingErrorDescription(selectedFile?.state?.error?.stage ?? '')}
                        // description={selectedFile?.state?.error?.message}
                        // details={formatDetails({ a: 's' })} // Todo: details
                        details={<pre>{selectedFile?.state?.error?.message}</pre>} // Todo: details
                    />
                </>}
                footer={<>
                    <Button
                        label={labels.close}
                        onClick={handleClose}
                    />
                </>}
                isOpen={isOpen}
                onClose={handleClose}
            />
        </div>
    );
};

export default withUIState(FileList);